export type AdminRole = 'super_admin' | 'admin';

export interface DashboardStats {
  totalUsers: number | null;
  totalBots: number | null;
  completedMatches: number | null;
  liveMatches: number | null;
  playersProvisioned: number | null;
  waitingRooms: number | null;
  /** Fields the server could not resolve; render these as "—", never as 0. */
  degraded: string[];
  cached: boolean;
}

export interface UserRow {
  id: string;
  userName: string;
  displayName: string;
  profileImgUrl: string | null;
  lastSeenAt: string | null;
  createdAt: string;
  status: string;
  providers: string[];
}

/** A player's gameplay stats, from card-inventory. Null until they first play. */
export interface UserStats {
  currency: number;
  xp: number;
  level: number;
  wins: number;
  losses: number;
  totalMatches: number;
  winStreak: number;
  loginStreak: number;
}

/** Summary of one recent match, newest first. `winnerId === null` is a draw. */
export interface RecentMatch {
  matchId: string;
  participantIds: string[];
  winnerId: string | null;
  durationMs: number;
  createdAt: string;
}

/** Everything shown on the user-detail page (BFF fans this out to inventory). */
export interface UserDetail {
  user: UserRow & { email: string | null };
  /** Null when the player has never played (no inventory profile yet). */
  stats: UserStats | null;
  /** False when the player has no inventory profile — "hasn't played yet". */
  provisioned: boolean;
  recentMatches: RecentMatch[];
  cards: OwnedCard[];
  decks: BotDeck[];
  degraded: string[];
  degradedReasons?: Record<string, string>;
}

export type BotPersonality = 'aggressive' | 'defensive' | 'combo' | 'balanced';

export interface BotRow {
  id: string;
  userName: string;
  displayName: string;
  profileImgUrl: string | null;
  level: number | null;
  defaultDeckId: string | null;
  botPersonality: BotPersonality | null;
  status: string;
  createdAt: string;
}

export interface OwnedCard {
  instanceId: string;
  cardId: string;
  variant: string;
  level: number;
  acquiredAt: string;
  /** Decks that pin this instance. Non-empty means it cannot be revoked. */
  inDeckIds: string[];
  name: string | null;
  cost: number | null;
  type: string | null;
  artUrl: string | null;
  missingFromCatalog: boolean;
}

export interface DeckCard {
  instanceId: string;
  cardId: string | null;
  variant?: string;
  level?: number;
  name?: string | null;
  cost?: number | null;
  type?: string | null;
  artUrl?: string | null;
  missing: boolean;
}

export interface BotDeck {
  deckId: string;
  name: string;
  isActive: boolean;
  instanceIds: string[];
  cards: DeckCard[];
}

export interface CatalogCard {
  cardId: string;
  name: string;
  cost: number;
  type: string;
  description?: string;
  artUrl?: string;
}

export interface AdminAccount {
  id: string;
  username: string;
  displayName: string;
  role: AdminRole;
  isActive: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

/** Number of cards a legal deck must contain (mirrors the server's DECK_SIZE). */
export const DECK_SIZE = 12;

// --- Contact messages -------------------------------------------------------

export type ContactSource = 'site' | 'game';

/** Row in the messages list — only name + subject are shown (plus read state). */
export interface ContactMessageRow {
  id: string;
  name: string;
  subject: string;
  read: boolean;
  createdAt: string;
}

/** Full message shown in the detail modal. */
export interface ContactMessageDetail {
  id: string;
  name: string;
  email: string | null;
  subject: string;
  message: string;
  source: ContactSource;
  read: boolean;
  ip: string | null;
  createdAt: string;
}
